import { IconInfoCircle } from '@tabler/icons-react';
import { AxiosError } from 'axios';

import { useEffect } from 'react';

import {
    Button,
    FileInput,
    Group,
    Modal,
    Select,
    TextInput,
    Tooltip
} from '@mantine/core';
import { useForm } from '@mantine/form';

import { DocumentDto, DocumentUploadDto, ProjectDto } from '../../api/models';
import { useGetApiNotionProjectProjectIdPhases } from '../../api/notion/notion.ts';
import {
    useGetApiSharePointMarkdownTemplates,
    useGetApiSharePointMarkdownTemplatesTemplateName,
    useGetApiSharePointTemplates
} from '../../api/share-point/share-point.ts';
import { defaultProjectCode } from '../utils/DateUtils.tsx';
import ServerLoadErrorFallback from '../utils/ServerLoadErrorFallback.tsx';

interface DocumentProps {
    opened: boolean;
    onClose: () => void;
    onSubmit: (data: DocumentUploadDto) => Promise<void>;
    error: AxiosError | null;
    isPending: boolean;
    refetch: () => void;
    project: ProjectDto;
    document?: DocumentDto;
    title: string;
    includeFileImport?: boolean;
}

const DocumentModal = ({
    opened,
    onClose,
    onSubmit,
    error,
    isPending,
    refetch,
    project,
    document,
    title,
    includeFileImport
}: DocumentProps) => {
    const {
        data: templates,
        error: templateError,
        isLoading: templateLoading
    } = useGetApiSharePointTemplates();

    // Use auto-generated orval hooks for markdown templates
    const {
        data: markdownTemplates,
        error: markdownTemplateError,
        isLoading: markdownTemplateLoading
    } = useGetApiSharePointMarkdownTemplates({
        query: {
            enabled: !includeFileImport, // Only fetch when not importing files
            retry: false, // Don't retry on 404 - it's expected when no templates exist
            throwOnError: false // Don't throw errors to error boundary for expected 404s
        }
    });

    // Check if error is a 404 (expected when no markdown templates exist)
    const isExpected404 =
        markdownTemplateError &&
        'response' in markdownTemplateError &&
        markdownTemplateError.response &&
        typeof markdownTemplateError.response === 'object' &&
        'status' in markdownTemplateError.response &&
        markdownTemplateError.response.status === 404;
    const shouldShowMarkdownTemplates = !markdownTemplateError || isExpected404;

    // Fetch project phases/subprojects from Notion
    const { data: projectPhases, isLoading: phasesLoading } =
        useGetApiNotionProjectProjectIdPhases(project.id, {
            query: {
                enabled: !!project.id,
                retry: false,
                throwOnError: false
            }
        });

    const form = useForm({
        initialValues: {
            documentName: '',
            documentTitle: '',
            documentNumber: '001',
            documentFile: null as File | null,
            template: null as string | null,
            markdownTemplate: null as string | null,
            notionPhaseId: null as string | null
        },
        validate: {
            documentName: (value: string) =>
                value.length < 2
                    ? 'Document name must have at least 2 characters'
                    : null,
            documentNumber: (value: string) =>
                value.length < 3
                    ? 'Document name must have at least 3 characters'
                    : null,
            documentFile: (value: File | null) =>
                includeFileImport
                    ? value == undefined
                        ? 'Please select a markdown file'
                        : null
                    : null,
            template: (value: string | null) =>
                value ? null : 'Please select a template'
        }
    });

    useEffect(() => {
        if (opened && document) {
            form.setValues({
                documentName: document.name,
                documentTitle: document.title,
                documentNumber: document.documentNumber,
                documentFile: null,
                template: document.template,
                markdownTemplate: document.markdownTemplate ?? null,
                notionPhaseId: document.notionPhaseId ?? null
            });
        } else if (opened && !document) {
            form.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, document]);

    // Hook to fetch template content when a markdown template is selected
    const { data: selectedTemplateContent, isLoading: templateContentLoading } =
        useGetApiSharePointMarkdownTemplatesTemplateName(
            form.values.markdownTemplate ?? '',
            {
                query: {
                    enabled:
                        !!form.values.markdownTemplate &&
                        !includeFileImport &&
                        !document,
                    retry: false,
                    throwOnError: false
                }
            }
        );

    const documentCode = (docNumber: string) => {
        const projectCode = project?.code ?? `XYZ-${defaultProjectCode()}`;
        return `${projectCode}-${docNumber}`;
    };

    const handleSubmit = async () => {
        let content: string;

        if (includeFileImport) {
            const docText = await form.values.documentFile?.text();
            if (docText) {
                content = docText;
            } else if (document) {
                content = document.content ?? '# Pitch';
            } else {
                content = '# Pitch';
            }
        } else if (form.values.markdownTemplate && !document) {
            // Use content from selected markdown template
            content = selectedTemplateContent ?? '# Pitch';
        } else {
            content = document?.content ?? '# Pitch';
        }

        let docTitle = form.values.documentTitle;

        if (!docTitle || docTitle.length === 0) {
            docTitle = form.values.documentName;
        }

        const data: DocumentUploadDto = {
            name: form.values.documentName,
            content,
            title: docTitle,
            documentNumber: form.values.documentNumber,
            documentCode: documentCode(form.values.documentNumber),
            titlePage: true,
            template: form.values.template ?? 'BlankTemplate',
            markdownTemplate: form.values.markdownTemplate ?? undefined,
            notionPhaseId: form.values.notionPhaseId ?? undefined
        };

        try {
            await onSubmit(data);
            form.reset();
            refetch();
            onClose();
        } catch (err) {
            console.error('Error submitting the document:', err);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title={title} closeOnClickOutside={false}>
            {(error ??
                templateError ??
                (markdownTemplateError && !isExpected404)) && (
                <ServerLoadErrorFallback
                    entity={title}
                    error={(error ?? templateError ?? markdownTemplateError)!}
                />
            )}
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <TextInput
                    mt='sm'
                    label={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            Document Name
                            <Tooltip label='Display name shown in the NavBar and used in the file name of exports.'>
                                <IconInfoCircle size={16} />
                            </Tooltip>
                        </div>
                    }
                    placeholder='Enter name'
                    {...form.getInputProps('documentName')}
                />
                <TextInput
                    mt='sm'
                    label={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            Document Title
                            <Tooltip label='Replaces %Document% in export template, Document Name is used when left blank.'>
                                <IconInfoCircle size={16} />
                            </Tooltip>
                        </div>
                    }
                    placeholder='Enter title'
                    {...form.getInputProps('documentTitle')}
                />
                <TextInput
                    mt='sm'
                    label={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            Document Number
                            <Tooltip label='Appended onto project code to construct the document code and replaces %Project% in export template'>
                                <IconInfoCircle size={16} />
                            </Tooltip>
                        </div>
                    }
                    placeholder='Enter document number'
                    {...form.getInputProps('documentNumber')}
                />
                {includeFileImport && (
                    <FileInput
                        mt='sm'
                        label={
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4
                                }}
                            >
                                Markdown File
                                <Tooltip label='Upload a .md file to prefill the content'>
                                    <IconInfoCircle size={16} />
                                </Tooltip>
                            </div>
                        }
                        placeholder='Select a file'
                        {...form.getInputProps('documentFile')}
                    />
                )}
                {/* Project Phase/Subproject Selection */}
                <Select
                    mt='sm'
                    label={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            Project Phase (Optional)
                            <Tooltip
                                label='Phases are sourced from a "Phases" table in the Notion project page. Selecting a phase scopes this document to that phase&#39;s user stories, risks, and cost data. Only needed for multi-phase projects.'
                                multiline
                                w={300}
                            >
                                <IconInfoCircle size={16} />
                            </Tooltip>
                        </div>
                    }
                    searchable
                    clearable
                    disabled={phasesLoading}
                    placeholder={
                        phasesLoading
                            ? 'Loading phases...'
                            : projectPhases?.length === 0
                              ? 'No phases available for this project'
                              : 'Select a phase (optional)'
                    }
                    data={
                        projectPhases?.map(phase => ({
                            value: phase.id ?? '',
                            label: phase.name ?? ''
                        })) ?? []
                    }
                    {...form.getInputProps('notionPhaseId')}
                />
                {!includeFileImport &&
                !document &&
                shouldShowMarkdownTemplates ? (
                    <Select
                        mt='sm'
                        label={
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4
                                }}
                            >
                                Markdown Template
                                <Tooltip label='Select a markdown template to pre-populate the document content'>
                                    <IconInfoCircle size={16} />
                                </Tooltip>
                            </div>
                        }
                        searchable
                        clearable
                        disabled={markdownTemplateLoading || !!isExpected404}
                        placeholder={
                            markdownTemplateLoading
                                ? 'Loading templates...'
                                : isExpected404 ||
                                    markdownTemplates?.length === 0
                                  ? 'No available markdown templates'
                                  : 'Select a markdown template (optional)'
                        }
                        data={markdownTemplates ?? []}
                        {...form.getInputProps('markdownTemplate')}
                    />
                ) : null}
                <Select
                    mt='sm'
                    label={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            Document Template
                            <Tooltip label='Template used when exporting, templates can be found on SharePoint'>
                                <IconInfoCircle size={16} />
                            </Tooltip>
                        </div>
                    }
                    searchable
                    disabled={templateLoading}
                    placeholder={
                        templateLoading
                            ? 'Loading templates...'
                            : templates?.length === 0
                              ? 'No available templates'
                              : 'Select a template'
                    }
                    data={templates ?? []}
                    {...form.getInputProps('template')}
                />
                <Group mt='lg' justify='flex-end'>
                    <Button onClick={onClose} variant='outline'>
                        Cancel
                    </Button>
                    <Button
                        type='submit'
                        variant='filled'
                        loading={isPending || templateContentLoading}
                    >
                        Submit
                    </Button>
                </Group>
            </form>
        </Modal>
    );
};

export default DocumentModal;
